// Pure tile <-> geographic coordinate math (framework-agnostic).
//
// Ported from the 1.0 viewer's ZoomToTile.tsx so Viewer 2.0 can reuse the
// same XYZ-tile navigation without depending on any React component. No
// rendering or MapLibre dependency — just arithmetic.

/** Half the width of the world in EPSG:3857 (Web Mercator) meters. */
const WORLD_MERC_MAX = 20037508.3427892;
const WORLD_MERC_MIN = -1 * WORLD_MERC_MAX;
const WORLD_MERC_SIZE = WORLD_MERC_MAX - WORLD_MERC_MIN;

/** A geographic point in WGS84 degrees. */
export interface LngLatPoint {
    lng: number;
    lat: number;
}

/** A slippy-map tile address. */
export interface TileCoord {
    x: number;
    y: number;
    z: number;
}

/** Converts an EPSG:3857 x meter value to WGS84 longitude degrees. */
export function mercatorXToLng(x: number): number {
    return (x * 180) / WORLD_MERC_MAX;
}

/** Converts an EPSG:3857 y meter value to WGS84 latitude degrees. */
export function mercatorYToLat(y: number): number {
    return (Math.atan(Math.exp((y * Math.PI) / WORLD_MERC_MAX)) * 360) / Math.PI - 90;
}

/**
 * Returns the WGS84 center of an XYZ tile.
 *
 * XYZ tile coordinates are in image space (origin top-left), so y increases
 * southward. Mirrors the 1.0 viewer's `tileToCenter` exactly.
 */
export function tileToCenter(tile: TileCoord): LngLatPoint {
    const worldTileSize = 2 ** tile.z;
    const tileMercSize = WORLD_MERC_SIZE / worldTileSize;

    const xminMerc = WORLD_MERC_MIN + tileMercSize * tile.x;
    const xmaxMerc = WORLD_MERC_MIN + tileMercSize * (tile.x + 1);
    // y is top-left origin: ymax uses y, ymin uses y+1.
    const yminMerc = WORLD_MERC_MAX - tileMercSize * (tile.y + 1);
    const ymaxMerc = WORLD_MERC_MAX - tileMercSize * tile.y;

    const lngMin = mercatorXToLng(xminMerc);
    const lngMax = mercatorXToLng(xmaxMerc);
    const latMin = mercatorYToLat(yminMerc);
    const latMax = mercatorYToLat(ymaxMerc);

    return {
        lng: (lngMin + lngMax) / 2,
        lat: (latMin + latMax) / 2,
    };
}

/**
 * Validates an XYZ tile address: non-negative integer x/y within the tile grid
 * for the zoom, and a zoom in [0, 24]. Returns true when navigable.
 */
export function isValidTile(tile: TileCoord): boolean {
    const { x, y, z } = tile;
    if (!Number.isInteger(x) || !Number.isInteger(y) || !Number.isInteger(z)) {
        return false;
    }
    if (z < 0 || z > 24) {
        return false;
    }
    const max = 2 ** z;
    return x >= 0 && x < max && y >= 0 && y < max;
}
